// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/* Global compliance summary (all partners) */
export function useAdminComplianceSummary() {
  return useQuery({
    queryKey: ['admin-hotel-compliance-summary'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*');
      if (error) throw error;
      const rows = data || [];
      const uniqueEvents = new Set(rows.map((r: any) => r.event_id)).size;
      return {
        total_events: uniqueEvents,
        total_teams: rows.reduce((s: number, r: any) => s + (r.total_teams || 0), 0),
        confirmed: rows.reduce((s: number, r: any) => s + (r.confirmed_bookings || 0), 0),
        unbooked: rows.reduce((s: number, r: any) => s + (r.unbooked_teams || 0), 0),
        avg_compliance: rows.length > 0
          ? Math.round(rows.reduce((s: number, r: any) => s + (r.compliance_pct || 0), 0) / rows.length)
          : 0,
        attrition_risks: rows.filter((r: any) => r.attrition_risk).length,
      };
    },
  });
}

/* Per-partner stats (userId = auth user id, joined to profiles) */
export function useAdminPartnerStats() {
  return useQuery({
    queryKey: ['admin-hotel-partner-stats'],
    queryFn: async () => {
      const { data: assignments, error: e1 } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('hotel_partner_user_id, event_id');
      if (e1) throw e1;

      const { data: bookings, error: e2 } = await (supabase as any)
        .from('hotel_bookings')
        .select('event_id, status, room_nights');
      if (e2) throw e2;

      const { data: compliance, error: e3 } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('hotel_partner_user_id, unbooked_teams, total_teams');
      if (e3) throw e3;

      const { data: outreach, error: e4 } = await (supabase as any)
        .from('hotel_outreach_log')
        .select('event_id, recipient_count');
      if (e4) throw e4;

      // Collect partner user ids from assignments, then fetch profiles
      const partnerIds = [...new Set((assignments || []).map((a: any) => a.hotel_partner_user_id).filter(Boolean))];
      let profileMap = new Map<string, any>();
      if (partnerIds.length > 0) {
        const { data: profs } = await (supabase as any)
          .from('profiles')
          .select('user_id, first_name, last_name, name, email')
          .in('user_id', partnerIds);
        profileMap = new Map((profs || []).map((p: any) => [p.user_id, p]));
      }

      const partnerMap = new Map<string, any>();
      (assignments || []).forEach((a: any) => {
        if (!a.hotel_partner_user_id) return;
        if (!partnerMap.has(a.hotel_partner_user_id)) {
          const p = profileMap.get(a.hotel_partner_user_id);
          partnerMap.set(a.hotel_partner_user_id, {
            user_id: a.hotel_partner_user_id,
            first_name: p?.first_name || '',
            last_name: p?.last_name || '',
            email: p?.email || '',
            events: 0,
            confirmed: 0,
            unbooked: 0,
            room_nights: 0,
            emails_sent: 0,
            eventIds: new Set(),
          });
        }
        const entry = partnerMap.get(a.hotel_partner_user_id)!;
        entry.eventIds.add(a.event_id);
      });

      // Count bookings per event, attribute to partner via assignments
      const eventPartnerMap = new Map<string, string>();
      (assignments || []).forEach((a: any) => eventPartnerMap.set(a.event_id, a.hotel_partner_user_id));

      (bookings || []).forEach((b: any) => {
        const partnerUserId = eventPartnerMap.get(b.event_id);
        if (!partnerUserId || !partnerMap.has(partnerUserId)) return;
        const entry = partnerMap.get(partnerUserId)!;
        if (b.status === 'confirmed') entry.confirmed++;
        entry.room_nights += b.room_nights || 0;
      });

      (compliance || []).forEach((c: any) => {
        if (!c.hotel_partner_user_id || !partnerMap.has(c.hotel_partner_user_id)) return;
        partnerMap.get(c.hotel_partner_user_id)!.unbooked += c.unbooked_teams || 0;
      });

      (outreach || []).forEach((o: any) => {
        const partnerUserId = eventPartnerMap.get(o.event_id);
        if (!partnerUserId || !partnerMap.has(partnerUserId)) return;
        partnerMap.get(partnerUserId)!.emails_sent += o.recipient_count || 0;
      });

      return Array.from(partnerMap.values()).map((p) => ({
        ...p,
        events: p.eventIds.size,
        eventIds: undefined,
      }));
    },
  });
}

/* Global compliance by event */
export function useAdminComplianceByEvent() {
  return useQuery({
    queryKey: ['admin-hotel-compliance-by-event'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

/* Global room nights */
export function useAdminRoomNights() {
  return useQuery({
    queryKey: ['admin-hotel-room-nights'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_bookings')
        .select('event_id, hotel_name, room_nights, number_of_rooms, events!inner(name)')
        .in('status', ['confirmed', 'pending']);
      if (error) throw error;
      return data || [];
    },
  });
}

/* Global outreach */
export function useAdminOutreach() {
  return useQuery({
    queryKey: ['admin-hotel-outreach'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_outreach_log')
        .select('*, events!inner(name)')
        .order('sent_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data || [];
    },
  });
}

/* Global rate expiry */
export function useAdminRateExpiry() {
  return useQuery({
    queryKey: ['admin-hotel-rate-expiry'],
    queryFn: async () => {
      const now = new Date();
      const in30 = new Date(now);
      in30.setDate(in30.getDate() + 30);
      const { data, error } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .not('group_rate_expiry', 'is', null)
        .lte('group_rate_expiry', in30.toISOString().split('T')[0]);
      if (error) throw error;
      return (data || []).map((d: any) => ({
        ...d,
        days_remaining: Math.ceil((new Date(d.group_rate_expiry).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
      }));
    },
  });
}

/* Global revenue */
export function useAdminRevenue() {
  return useQuery({
    queryKey: ['admin-hotel-revenue'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_revenue_summary')
        .select('*, events!inner(name), hotel_properties(hotel_name)');
      if (error) throw error;
      return data || [];
    },
  });
}
